import { useTranslation } from 'react-i18next'
import useQuery from 'hooks/useQuery'

/**
 * Indicador puntual de estado de ARCA (pedido de Nacho, 2026-08-05) — a
 * diferencia del aviso condicional (`arcaDown && <alert>`, que ya existe en
 * Facturar.js/Arca.js y solo aparece cuando ARCA está caído), esto es un
 * punto SIEMPRE visible con tooltip, para que el profesional pueda chequear
 * el estado de un vistazo antes de facturar o vincular, no solo enterarse
 * después de que algo ya falló. Se usa en TODO lugar donde se emite un
 * comprobante o se llama a un servicio real de ARCA/AFIP (Facturar.js
 * individual y en lote, Arca.js vinculación) — no reemplaza esos avisos
 * condicionales, los complementa.
 *
 * `GET /arca/health` no depende de que el team tenga el feature flag
 * (mismo criterio que el resto de los healthQuery ya existentes) — el
 * caller es responsable de solo renderizar esto donde ARCA ya está
 * habilitado (mismo patrón que Facturar.js/Arca.js).
 */
const COLOR = {
  up: '#28a745',
  down: '#dc3545',
  unknown: '#adb5bd',
}

export function ArcaStatusIndicator() {
  const { t } = useTranslation()
  // Bug real (auditoría 2026-08-14): staleTime sin refetchInterval sólo
  // controla si un refetch on-focus/on-mount/on-reconnect cuenta como
  // "necesario" — no dispara un refetch por sí solo. Este indicador vive
  // pegado en la toolbar de pantallas que quedan abiertas mucho tiempo
  // (Facturar, Arca) sin re-montarse ni perder foco — sin refetchInterval
  // el puntito quedaba mostrando el estado del momento en que se montó,
  // para siempre, aunque ARCA se caiga o se recupere después.
  const healthQuery = useQuery(window._l('/arca/health').substr(1), { staleTime: 30000, refetchInterval: 30000 })

  // Mientras carga (o si la propia query de health falla) no afirmamos nada
  // — ni verde ni rojo — es lo que ya usa arcaDown === false en el resto del
  // módulo (undefined ≠ false), acá con un color propio en vez de ocultarse.
  const status = healthQuery.data?.up === true ? 'up' : healthQuery.data?.up === false ? 'down' : 'unknown'

  const tooltip = {
    up: t('arca_status_up', 'ARCA está operativo.'),
    down: t('arca_status_down', 'ARCA está con problemas — las emisiones quedan en cola hasta que se recupere.'),
    unknown: t('arca_status_unknown', 'No pudimos confirmar el estado de ARCA todavía.'),
  }[status]

  return (
    <span
      data-testid="arca-status-indicator"
      data-status={status}
      data-tooltip-id="app-tooltip"
      data-tooltip-content={tooltip}
      role="img"
      aria-label={tooltip}
      style={{
        display: 'inline-block',
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        backgroundColor: COLOR[status],
        cursor: 'help',
      }}
    />
  )
}

export default ArcaStatusIndicator
